<template>
  <div class="email-validation">
    <AkHeading3 class="email-validation__title">
      {{ $t('signup.step.email-validation.title') }}
    </AkHeading3>

    <AkParagraph class="email-validation__description">
      {{ $t('signup.step.email-validation.description') }}
      <strong>{{ email }}</strong>
    </AkParagraph>

    <!--
      role="status" lives on the wrapper, not on the confirmation span: a live region must already
      exist in the DOM before its content changes to be announced reliably, and live regions do not
      announce their initial content — so the idle text stays silent and only the swap is announced.
    -->
    <AkParagraph
      class="email-validation__resend"
      role="status"
    >
      <span
        v-if="resendConfirmed"
        class="email-validation__resend-confirmation"
        data-testid="resend-confirmation"
      >
        <AkIcon
          symbol="check-circle"
          size="md"
          class="ds-text-success-700"
        />
        {{ $t('signup.step.email-validation.code-resent', { email }) }}
      </span>
      <template v-else>
        {{ $t('signup.step.email-validation.didnt-receive') }}
        <span
          v-if="isResending"
          class="email-validation__resend-sending"
          data-testid="resend-in-progress"
        >
          {{ $t('signup.step.email-validation.resending') }}
        </span>
        <AkLink
          v-else
          link
          color="info"
          data-testid="resend-code-button"
          @click="onResendCode"
        >
          {{ $t('signup.step.email-validation.resend') }}
        </AkLink>
      </template>
    </AkParagraph>

    <div class="email-validation__code-inputs">
      <input
        v-for="(_digit, index) in codeDigits"
        :key="index"
        ref="inputs"
        v-model="codeDigits[index]"
        placeholder="0"
        type="text"
        inputmode="numeric"
        maxlength="1"
        class="email-validation__digit-input"
        :data-testid="`code-input-${index}`"
        @input="onDigitInput(index)"
        @keydown="onKeyDown($event, index)"
        @paste="onPaste"
      >
    </div>

    <div
      v-if="error"
      class="global-error"
      data-testid="error-message"
    >
      {{ error }}
    </div>

    <div class="email-validation__actions">
      <AkButton
        outlined
        size="lg"
        data-testid="back-button"
        @click="onBack"
      >
        {{ $t('signup.step.email-validation.back') }}
      </AkButton>
      <AkButton
        size="lg"
        class="email-validation__verify-button"
        data-testid="verify-button"
        :loading="isVerifying"
        :disabled="!isCodeComplete || isVerifying || isResending"
        @click="onVerify"
      >
        {{ $t('signup.step.email-validation.verify') }}
      </AkButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue';
import { AkButton, AkIcon } from '@ankorstore/design-system';
import Analytics from '@monolith/legacy/services/analytics';
import { SignUpEventNames } from '@monolith/legacy/services/analytics/events/sign-up/common/event-names';
import SignUpEvent from '@monolith/legacy/services/analytics/events/sign-up/sign-up-event';
import type { SignupAudience } from '@monolith/legacy/types/signup-audience';

interface Props {
  email: string;
  /** Which funnel this verification step belongs to; owned by auth-popin.vue. */
  audience: SignupAudience;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'verify', code: string): void;
  (e: 'resend'): void;
  (e: 'back', email: string): void;
}>();

const inputs = ref<HTMLInputElement[]>([]);
const codeDigits = ref<string[]>(['', '', '', '', '', '']);
const error = ref<string>('');
const isVerifying = ref(false);
const isResending = ref(false);
const resendConfirmed = ref(false);

/** How long the inline "code resent" confirmation stays visible before the link comes back. */
const RESEND_CONFIRMATION_DURATION_MS = 5000;
let resendConfirmationTimer: ReturnType<typeof setTimeout> | null = null;

const isCodeComplete = computed(() => {
  return codeDigits.value.every((digit) => digit !== '');
});

const fullCode = computed(() => {
  return codeDigits.value.join('');
});

onMounted(() => {
  Analytics.track(
    new SignUpEvent(SignUpEventNames.EmailValidationViewedEvent, {
      type: 'Element Viewed',
      audience: props.audience,
    })
  );
  nextTick(() => {
    inputs.value[0]?.focus();
  });
});

function clearResendConfirmation(): void {
  if (resendConfirmationTimer) {
    clearTimeout(resendConfirmationTimer);
    resendConfirmationTimer = null;
  }
  resendConfirmed.value = false;
}

function resetCode(): void {
  codeDigits.value = ['', '', '', '', '', ''];
  error.value = '';
}

function onDigitInput(index: number): void {
  const value = codeDigits.value[index];

  // Only allow digits
  if (value && !/^\d$/.test(value)) {
    codeDigits.value[index] = '';
    return;
  }

  // Move to next input if digit entered
  if (value && index < 5) {
    nextTick(() => {
      inputs.value[index + 1]?.focus();
    });
  }
}

function onKeyDown(event: KeyboardEvent, index: number): void {
  // Handle backspace
  if (event.key === 'Backspace' && !codeDigits.value[index] && index > 0) {
    nextTick(() => {
      inputs.value[index - 1]?.focus();
    });
  }

  // Handle arrow keys
  if (event.key === 'ArrowLeft' && index > 0) {
    event.preventDefault();
    inputs.value[index - 1]?.focus();
  }
  if (event.key === 'ArrowRight' && index < 5) {
    event.preventDefault();
    inputs.value[index + 1]?.focus();
  }
}

function onPaste(event: ClipboardEvent): void {
  event.preventDefault();
  const pastedData = event.clipboardData?.getData('text') || '';
  const digits = pastedData.replace(/\D/g, '').slice(0, 6).split('');

  digits.forEach((digit, index) => {
    if (index < 6) {
      codeDigits.value[index] = digit;
    }
  });

  // Focus the next empty input or the last one
  const nextEmptyIndex = codeDigits.value.findIndex((d) => d === '');
  const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
  nextTick(() => {
    inputs.value[focusIndex]?.focus();
  });
}

function onVerify(): void {
  if (!isCodeComplete.value) {return;}

  Analytics.track(
    new SignUpEvent(SignUpEventNames.EmailValidationVerifyClickedEvent, {
      type: 'Click',
      audience: props.audience,
    })
  );
  error.value = '';
  isVerifying.value = true;
  emit('verify', fullCode.value);
}

function onResendCode(): void {
  // Belt-and-braces: the template already hides the link while a resend is in flight (AkLink
  // declares no `disabled` prop, so passing one to it did nothing). This guards programmatic
  // calls and keeps a double-fire from emitting twice or duplicating the analytics event.
  if (isResending.value) {
    return;
  }

  Analytics.track(
    new SignUpEvent(SignUpEventNames.EmailValidationResendClickedEvent, {
      type: 'Click',
      audience: props.audience,
    })
  );
  clearResendConfirmation();
  isResending.value = true;
  emit('resend');
}

function onBack(): void {
  Analytics.track(
    new SignUpEvent(SignUpEventNames.EmailValidationBackClickedEvent, {
      type: 'Click',
      audience: props.audience,
    })
  );
  emit('back', props.email);
}

function setError(message: string): void {
  error.value = message;
  isVerifying.value = false;
  // A verify error must never render next to a stale "code resent" line.
  clearResendConfirmation();
}

/**
 * Called by the host once a resend actually succeeded. Clears the digits on purpose: the resend
 * minted a fresh token, so anything already typed would now fail as "invalid or expired".
 */
function setResendSucceeded(): void {
  isResending.value = false;
  resetCode();
  resendConfirmed.value = true;

  if (resendConfirmationTimer) {
    clearTimeout(resendConfirmationTimer);
  }
  resendConfirmationTimer = setTimeout(() => {
    resendConfirmed.value = false;
    resendConfirmationTimer = null;
  }, RESEND_CONFIRMATION_DURATION_MS);

  nextTick(() => {
    inputs.value[0]?.focus();
  });
}

function setResending(value: boolean): void {
  isResending.value = value;
}

function setVerifying(value: boolean): void {
  isVerifying.value = value;
}

onUnmounted(() => {
  if (resendConfirmationTimer) {
    clearTimeout(resendConfirmationTimer);
  }
});

defineExpose({
  resetCode,
  setError,
  setResendSucceeded,
  setResending,
  setVerifying,
});
</script>

<style scoped lang="scss">
@import '@css/vue-import';

.email-validation {
  @apply ds-text-center ds-px-4 ds-py-2;

  &__title {
    @apply ds-font-bold ds-mb-6;
  }

  &__description {
    @apply ds-mb-4;
  }

  &__resend {
    @apply ds-mb-8;
  }

  &__resend-confirmation {
    @apply ds-inline-flex ds-items-center ds-gap-1 ds-text-success-700;
  }

  &__resend-sending {
    @apply ds-text-neutral-700;
  }

  &__code-inputs {
    @apply ds-flex ds-justify-center ds-gap-1 ds-mb-8;
  }

  &__digit-input {
    @apply ds-w-[48px] ds-h-[58px] md:ds-w-[56px] md:ds-h-[72px] ds-text-center ds-text-2xl ds-font-medium;
    @apply ds-border ds-border-solid ds-border-neutral-600 ds-rounded-md;
    @apply ds-outline-none;

    &:not(:placeholder-shown) {
      @apply ds-border-primary;
    }

    &:focus {
      @apply ds-border-accent ds-border-2;

      &::placeholder {
        @apply ds-text-transparent;
      }
    }
  }

  &__actions {
    @apply ds-flex ds-flex-col-reverse ds-gap-4 md:ds-flex-row ds-justify-center;
  }

  &__verify-button {
    @apply ds-bg-primary ds-text-white;
  }
}

.global-error {
  @apply ds-text-error-700 ds-text-sm ds-mb-4;
}
</style>
